import type { TOptionTypeUi } from 'components/EditableFields/EditableFields.types';
import { docHtmlConvertor, policyContentConverter, policyOptionConverter } from 'data/converters/policy.converters';
import {
  TPageableDataWithContentUi,
  TPageableDataWithContent,
  TPageableDataParamUI,
} from 'data/types/pagableData.type';
import {
  TCreatePolicyRequestUI,
  TDocHtmlUI,
  TPolicyContentGetDB,
  TPolicyContentGetUI,
  TPolicyGetShortData,
  TSuggestionTextUI,
  TUpdatePolicyRequestUI,
} from 'data/types/policy.types';
import { EPolicyUrls } from 'data/urls/policy.url';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { pageableDataConverter } from 'data/converters/pagableData.converters';
import { queryParamConverter } from 'data/converters/general.converters';
import { sortWithAllStatesFirst } from 'data/datas/policyState.data';
import { TPaymentSettings } from 'data/types/property.types';
import { authSplitApi } from '../helpers/slice.helpers';

const baseUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPolicyUrls.POLICY}`;
const suggestionsUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EPolicyUrls.POLICY_CONTENT_SUGGESTION}`;

export const policyApi = authSplitApi('policy', ['policyOptions', 'policyContent', 'suggestions']).injectEndpoints({
  endpoints: (build) => ({
    getPolicyOptions: build.query<TOptionTypeUi[], void>({
      query() {
        return {
          url: `${baseUrl}/${EPolicyUrls.SEARCH}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPolicyGetShortData[]) => {
        return data?.map((item) => policyOptionConverter.fromDb(item)) || [];
      },
      providesTags: ['policyOptions'],
    }),
    getDocHtml: build.query<TDocHtmlUI, { animalType: string; policyId: string }>({
      query({ animalType, policyId }) {
        return {
          url: `${baseUrl}/${EPolicyUrls.DOC}/${animalType}`,
          method: 'GET',
          responseHandler: 'text',
          params: { policyId },
        };
      },
      transformResponse: (data: string) => docHtmlConvertor.fromDb(data),
    }),
    getPolicies: build.query<TPageableDataWithContentUi<TPolicyContentGetUI[]>, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: `${baseUrl}`,
          method: 'GET',
          params: queryParamConverter(search, pagination),
        };
      },
      transformResponse: (data: TPageableDataWithContent<TPolicyContentGetDB[]>) => {
        const { content, ...other } = data;
        return {
          content: content.map((item) => policyContentConverter.fromDb(item)),
          paginationData: pageableDataConverter.fromDb(other),
        };
      },
      providesTags: ['policyContent'],
    }),
    deletePolicy: build.mutation<void, { policyId: string }>({
      query({ policyId }) {
        return {
          url: `${baseUrl}/${policyId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['policyContent', 'policyOptions'],
    }),
    getPolicy: build.query<TPolicyContentGetUI, { policyId: string }>({
      query({ policyId }) {
        return {
          url: `${baseUrl}/${policyId}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TPolicyContentGetDB) => policyContentConverter.fromDb(data),
    }),
    updatePolicy: build.mutation<void, TUpdatePolicyRequestUI>({
      query(body) {
        return {
          url: `${baseUrl}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['policyContent', 'policyOptions'],
    }),
    createPolicy: build.mutation<string, TCreatePolicyRequestUI>({
      query(body) {
        return {
          url: `${baseUrl}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['policyContent', 'policyOptions'],
    }),
    getSupportedStates: build.query<string[], void>({
      query() {
        return {
          url: `${baseUrl}/${EPolicyUrls.SUPPORTED_STATES}`,
          method: 'GET',
        };
      },
      transformResponse: (data: string[]) =>
        sortWithAllStatesFirst(
          data.map((state) =>
            state
              .toLowerCase()
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          ),
          (state) => state
        ),
    }),
    getSuggestions: build.query<TSuggestionTextUI[], void>({
      query() {
        return {
          url: `${suggestionsUrl}`,
          method: 'GET',
        };
      },
      providesTags: ['suggestions'],
    }),
    getChargeInfo: build.query<TPaymentSettings, { policyId: string }>({
      query(params) {
        return {
          url: `${baseUrl}/${EPolicyUrls.CHARGE_INFO}`,
          method: 'GET',
          params,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPolicyOptionsQuery,
  useGetDocHtmlQuery,
  useGetPoliciesQuery,
  useDeletePolicyMutation,
  useLazyGetPolicyQuery,
  useUpdatePolicyMutation,
  useCreatePolicyMutation,
  useGetSupportedStatesQuery,
  useGetSuggestionsQuery,
  useLazyGetChargeInfoQuery,
} = policyApi;
